import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonGrid,
    IonCol,
    IonRow,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    useIonRouter
} from '@ionic/react';
import {
    peopleOutline,
    barChartOutline,
    settingsOutline,
    analyticsOutline,
    shieldCheckmarkOutline,
    documentTextOutline,
    trendingUpOutline,
    eyeOutline,
    checkmarkCircleOutline,
    warningOutline
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getCurrentOrganizationId, getCurrentUserId, buildOrganizationParams } from '../helpers/organizationUtils';
import API_CONFIG from '../config/api';
import './View.css'; // Reuse the same styling
import Loader from '../components/Loader';

const Admin = () => {
    const router = useIonRouter();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalPolls: 0,
        totalVotes: 0,
        activePolls: 0
    });
    const [organizationName, setOrganizationName] = useState('');

    useEffect(() => {
        const roleId = String(sessionStorage.getItem('role_id') || localStorage.getItem('roleId') || '');
        if (!sessionStorage.getItem('user_id')) {
            router.push('/page/login');
            return;
        }

        // If they are an Org Admin (2), they should be in the Org Portal instead
        if (roleId === '2') {
            router.push('/org-admin');
            return;
        }

        // Get organization name for display
        const orgName = sessionStorage.getItem('organization_name');
        if (orgName) {
            setOrganizationName(orgName);
        }

        fetchAdminStats();
    }, []);

    const fetchAdminStats = async () => {
        setLoading(true);
        try {
            const userId = getCurrentUserId();
            const roleId = String(sessionStorage.getItem('role_id') || localStorage.getItem('roleId') || '');

            if (!userId) {
                console.log('No user context, showing empty stats');
                setLoading(false);
                return;
            }

            // For Super Admin (Role 1), use the dedicated all-time stats endpoint
            if (roleId === '1') {
                const response = await axios.get(`${API_CONFIG.GET_SUPER_ADMIN_STATS}?user_id=${userId}`);
                if (response.data.success) {
                    const data = response.data.stats;
                    setStats({
                        totalUsers: data.totalUsers || 0,
                        totalPolls: data.totalPolls || 0,
                        totalVotes: data.totalVotes || 0,
                        activePolls: data.activePolls || 0
                    });
                } else {
                    console.error('Failed to load Super Admin stats:', response.data.error);
                }
            } else {
                // For Organization Admins or others, use filtered analytics
                const organizationId = getCurrentOrganizationId();
                const params = new URLSearchParams();
                params.append('user_id', userId);
                params.append('period', '30days');
                
                if (organizationId) {
                    params.append('organization_id', organizationId);
                }

                const analyticsResponse = await axios.get(`${API_CONFIG.ANALYTICS}?${params.toString()}`);

                if (analyticsResponse.data.success) {
                    const data = analyticsResponse.data;
                    setStats({
                        totalUsers: parseInt(data.overview.total_users) || 0,
                        totalPolls: parseInt(data.overview.total_polls) || 0,
                        totalVotes: parseInt(data.overview.total_votes) || 0,
                        activePolls: parseInt(data.poll_stats.active) || 0
                    });
                } else {
                    console.error('Failed to load analytics:', analyticsResponse.data.error);
                }
            }
        } catch (error) {
            console.error('Error fetching admin stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const adminFeatures = [
        {
            title: 'User Management',
            subtitle: 'Manage user accounts and permissions',
            icon: peopleOutline,
            color: '#3b82f6',
            bgGradient: 'from-blue-500 to-blue-600',
            route: '/admin/users',
            description: 'View, edit, and manage all user accounts with advanced filtering and bulk operations.'
        },
        {
            title: 'Poll Management',
            subtitle: 'Monitor and manage all polls',
            icon: barChartOutline,
            color: '#10b981',
            bgGradient: 'from-emerald-500 to-emerald-600',
            route: '/admin/polls',
            description: 'Comprehensive poll analytics, content moderation, and lifecycle management tools.'
        },
        {
            title: 'Analytics & Reports',
            subtitle: 'Comprehensive data insights',
            icon: analyticsOutline,
            color: '#6366f1',
            bgGradient: 'from-indigo-500 to-indigo-600',
            route: '/admin/analytics',
            description: 'Generate detailed reports, view trends, and analyze user engagement patterns.'
        },
        {
            title: 'Content Moderation',
            subtitle: 'Review and moderate content',
            icon: shieldCheckmarkOutline,
            color: '#f59e0b',
            bgGradient: 'from-amber-500 to-amber-600',
            route: '/admin/moderation',
            description: 'Monitor polls for inappropriate content and maintain platform quality standards.'
        },
        {
            title: 'System Settings',
            subtitle: 'Configure application settings',
            icon: settingsOutline,
            color: '#ef4444',
            bgGradient: 'from-red-500 to-red-600',
            route: '/admin/settings',
            description: 'Manage app configuration, user limits, and system-wide preferences.'
        },
        {
            title: 'Documentation',
            subtitle: 'Admin guides and documentation',
            icon: documentTextOutline,
            color: '#06b6d4',
            bgGradient: 'from-cyan-500 to-cyan-600',
            route: '/admin/docs',
            description: 'Access comprehensive admin guides, API documentation, and help resources.'
        }
    ];

    const navigationCards = (feature) => {
        router.push(feature.route);
    };

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text">
                                {sessionStorage.getItem('role_id') === '1' ? 'Super Admin Dashboard' : 'Admin Dashboard'}
                            </span>
                        </div>
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Loading Admin Dashboard..." />}
                <div className="polls-container">

                    {/* Enhanced Admin Stats with Modern Design */}
                    <div className="mb-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {/* Total Users Card */}
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                        <IonIcon icon={peopleOutline} className="text-2xl" />
                                    </div>
                                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                                        <span className="text-xs font-medium">+12%</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-3xl font-bold">{stats.totalUsers}</h3>
                                    <p className="text-blue-100 text-sm font-medium">Total Users</p>
                                    <p className="text-blue-200 text-xs">Active this month</p>
                                </div>
                            </div>

                            {/* Total Polls Card */}
                            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                        <IonIcon icon={barChartOutline} className="text-2xl" />
                                    </div>
                                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                                        <span className="text-xs font-medium">+{stats.totalPolls}</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-3xl font-bold">{stats.totalPolls}</h3>
                                    <p className="text-emerald-100 text-sm font-medium">Total Polls</p>
                                    <p className="text-emerald-200 text-xs">All time created</p>
                                </div>
                            </div>

                            {/* Total Votes Card */}
                            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                        <IonIcon icon={trendingUpOutline} className="text-2xl" />
                                    </div>
                                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                                        <span className="text-xs font-medium">Live</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-3xl font-bold">{stats.totalVotes.toLocaleString()}</h3>
                                    <p className="text-indigo-100 text-sm font-medium">Total Votes</p>
                                    <p className="text-indigo-200 text-xs">Engagement rate 94%</p>
                                </div>
                            </div>

                            {/* Active Polls Card */}
                            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                        <IonIcon icon={checkmarkCircleOutline} className="text-2xl" />
                                    </div>
                                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1">
                                        <span className="text-xs font-medium">Live</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-3xl font-bold">{stats.activePolls}</h3>
                                    <p className="text-amber-100 text-sm font-medium">Active Polls</p>
                                    <p className="text-amber-200 text-xs">Currently running</p>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions Bar */}
                        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <button onClick={() => router.push('/admin/analytics')} className="flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                    <IonIcon icon={eyeOutline} />
                                    <span className="font-medium text-sm">View Reports</span>
                                </button>
                                <button onClick={() => router.push('/admin/users')} className="flex items-center justify-center space-x-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                    <IonIcon icon={peopleOutline} />
                                    <span className="font-medium text-sm">Manage Users</span>
                                </button>
                                <button onClick={() => router.push('/admin/polls')} className="flex items-center justify-center space-x-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                    <IonIcon icon={barChartOutline} />
                                    <span className="font-medium text-sm">Poll Analytics</span>
                                </button>
                                <button onClick={() => router.push('/admin/moderation')} className="flex items-center justify-center space-x-2 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                    <IonIcon icon={warningOutline} />
                                    <span className="font-medium text-sm">Moderation</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Admin Features Grid */}
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Administration Tools</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {adminFeatures.map((feature, index) => (
                                <div
                                    key={index}
                                    className="group cursor-pointer"
                                    onClick={() => navigationCards(feature)}
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                                        {/* Gradient Header */}
                                        <div className={`bg-gradient-to-r ${feature.bgGradient} p-6 text-white`}>
                                            <div className="flex items-center space-x-4 mb-3">
                                                <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                                    <IonIcon icon={feature.icon} className="text-2xl" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold">{feature.title}</h3>
                                                    <p className="text-sm opacity-90">{feature.subtitle}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="p-6">
                                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                                {feature.description}
                                            </p>

                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                                    Available Now
                                                </span>
                                                <div className="text-sm font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
                                                    Explore →
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* System Status */}
                    <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="bg-green-500 rounded-full p-2">
                                <IonIcon icon={checkmarkCircleOutline} className="text-white text-lg" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">System Status</h3>
                                <p className="text-sm text-gray-600">All systems operational</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-2xl font-bold text-green-600">99.9%</div>
                                <div className="text-xs text-gray-500">Uptime</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-blue-600">45ms</div>
                                <div className="text-xs text-gray-500">Response Time</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-indigo-600">1.2k</div>
                                <div className="text-xs text-gray-500">Active Sessions</div>
                            </div>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default Admin;
